import { useState, useEffect, useRef } from 'react'
import { subscribeToGame, deleteGame } from '../utils/firebaseGame'
import { IconClapper, IconCheck, IconCopy, IconMasks, IconClock } from './icons'

export default function MultiplayerLobby({ gameId, code, role, gameType = 'h2h', onOpponentJoined, onAbandon }) {
  const [copied, setCopied] = useState(false)
  const [opponentJoining, setOpponentJoining] = useState(false) // brief "joined!" flash for P1
  const advancedRef = useRef(false) // prevent double-fire

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard not available — silently ignore
    }
  }

  const handleAbandon = async () => {
    try { await deleteGame(gameId) } catch {}
    onAbandon()
  }

  // P2: auto-advance to actor pick after a brief "You're in!" pause
  useEffect(() => {
    if (role !== 'p2') return
    const t = setTimeout(() => onOpponentJoined(), 2000)
    return () => clearTimeout(t)
  }, [role]) // eslint-disable-line react-hooks/exhaustive-deps

  // P1 only: listen for P2 joining (status → 'picking')
  useEffect(() => {
    if (role !== 'p1' || !gameId) return

    const unsub = subscribeToGame(gameId, (game) => {
      if (game.status === 'picking' && !advancedRef.current) {
        advancedRef.current = true
        setOpponentJoining(true)
        setTimeout(() => onOpponentJoined(), 2000)
      }
    })

    return () => unsub()
  }, [gameId, role, onOpponentJoined])

  return (
    <div className="lobby">

      {role === 'p1' && !opponentJoining && (
        <>
          <h2 className="lobby__heading">Game Created! <IconClapper size={18} style={{ verticalAlign: 'middle' }} /></h2>
          <p className="lobby__sub">Share this code with your opponent</p>

          <div className="lobby__code-box">
            <span className="lobby__code">{code}</span>
            <button
              type="button"
              className="lobby__copy-btn"
              onClick={handleCopy}
            >
              {copied ? <><IconCheck size={14} style={{ verticalAlign: 'middle' }} /> Copied!</> : <><IconCopy size={14} style={{ verticalAlign: 'middle' }} /> Copy Code</>}
            </button>
          </div>

          <p className="lobby__waiting">
            {gameType === 'coop' ? 'Waiting for your co-star to join…' : 'Waiting for opponent to join…'}
          </p>

          <button
            type="button"
            className="btn-ghost lobby__abandon"
            onClick={handleAbandon}
          >
            Abandon Game
          </button>
        </>
      )}

      {role === 'p1' && opponentJoining && (
        <>
          <div className="lobby__joined-banner">
            <span className="lobby__joined-icon"><IconMasks size={28} /></span>
            <p className="lobby__joined-title">Opponent joined!</p>
            <p className="lobby__joined-sub">Get ready to pick your actor…</p>
          </div>
        </>
      )}

      {role === 'p2' && (
        <>
          <h2 className="lobby__heading">You're in! <IconClapper size={18} style={{ verticalAlign: 'middle' }} /></h2>
          <p className="lobby__sub">Game code</p>

          <div className="lobby__code-box">
            <span className="lobby__code">{code}</span>
          </div>

          <div className="lobby__joined-banner">
            <span className="lobby__joined-icon"><IconClock size={28} /></span>
            <p className="lobby__joined-title">Waiting for host to start…</p>
            <p className="lobby__joined-sub">
              {gameType === 'coop'
                ? "You're in! Get ready to pick your actor."
                : 'Get ready to pick your actor.'}
            </p>
          </div>

          <button
            type="button"
            className="btn-ghost lobby__abandon"
            onClick={handleAbandon}
          >
            Leave Game
          </button>
        </>
      )}

    </div>
  )
}
